import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Localization } from 'connex-cds';
import { DataDenseGrid } from '@cai/material';
import { DynamicTimePeriodMenu } from '@cai/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { supportedLocales } from '../../../../../i18n/common';
import { useListDriverTickets } from '../../../../../query-hooks/tickets';
import { all_date_options, useTableColumns } from './config';
import { TicketListFooter } from './components/TableFooter';
import useThemeConfig from '../useThemeConfig';
import dayjs from 'dayjs';
import * as S from './styles';

const componentAction = ['prev', 'next'];

const TicketList = () => {
  const tableRef = useRef(null);
  const { userLocale, timeZone } = Localization.useLocalizationContext();
  const [queryParams, setQueryParams] = useState({ dateOptions: 'Today' });

  const navigate = useNavigate();

  const tickets = useListDriverTickets(queryParams);

  const sendMessage = message => {
    window.postMessage(message, '*');
  };

  const setDateTypeFilter = (option, date) => {
    const _queryParams = {};
    if (dayjs(date).isValid() && componentAction.includes(option)) {
      const startDateTimeStamp = new Date(date).setHours(0, 0, 0);
      const startDate = new Date(startDateTimeStamp).toISOString();

      const endDateTimestamp = new Date(date).setHours(23, 59, 59);
      const endDate = new Date(endDateTimestamp).toISOString();

      _queryParams.startDate = startDate;
      _queryParams.endDate = endDate;
      setQueryParams(_queryParams);
    } else {
      _queryParams.dateOption = option;
      setQueryParams(_queryParams);
    }
  };

  const getTableRows = useCallback(
    tickets => {
      return tickets.map((ticket, index) => ({
        index: index,
        id: ticket?.crn,
        crn: ticket?.crn,
        createDate: ticket?.createDate,
        ticketId: ticket?.id,
        customerStatus: ticket?.customerStatus?.statusCode,
        vehicleId: ticket?.vehicleId,
        isVoided: ticket?.isVoided,
        timeZone: ticket?.origin?.timeZone || timeZone,
      }));
    },
    [timeZone]
  );

  const handleRedirectToTicketDetails = useCallback(
    ({ row }) => {
      const { crn, vehicleId, isVoided } = row || {};
      if (isVoided) return;

      if (vehicleId && crn) {
        const message = { type: 'ticket', active: true, crn };

        navigate(`../${vehicleId}`, {
          state: {
            type: 'ticket',
            active: true,
            crn: crn,
          },
        });
        sendMessage(message);
      }
    },
    [navigate]
  );

  const refreshTickets = useCallback(() => {
    tickets.refetch();
  }, [tickets.refetch]);

  const ticketsList = useMemo(() => {
    if (tickets?.data && tickets?.data?.length > 0) {
      return getTableRows(tickets?.data);
    }
    return [];
  }, [tickets?.data, getTableRows]);

  const isLoadingOrRefetching = useMemo(
    () => tickets?.isLoading || tickets?.isFetching || tickets?.isRefetching,
    [tickets?.isLoading, tickets?.isFetching, tickets?.isRefetching]
  );

  const ticketListFooterSlotProps = useMemo(
    () => ({
      refetch: refreshTickets,
      retrieved: tickets?.data?.length ?? 0,
      isLoadingOrRefetching,
    }),
    [refreshTickets, tickets?.data?.length, isLoadingOrRefetching]
  );

  return (
    <S.FormStyle>
      <S.TimePickerStyle>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={supportedLocales?.[userLocale]?.name}>
          <S.RightWrap>
            <DynamicTimePeriodMenu
              key="deliveries-date-range-picker"
              format={supportedLocales?.[userLocale]?.formats?.L ?? 'MM/DD/YYYY'}
              timezone={timeZone}
              options={all_date_options()}
              callbackFn={setDateTypeFilter}
              disabled={isLoadingOrRefetching}
            />
          </S.RightWrap>
        </LocalizationProvider>
      </S.TimePickerStyle>
      <S.GridStyle ref={tableRef}>
        <DataDenseGrid
          variant="base"
          id="tickets-list-view"
          columns={useTableColumns(tableRef.current)}
          loading={tickets?.isLoading}
          rows={ticketsList}
          onRowClick={handleRedirectToTicketDetails}
          withRefreshDataButton={false}
          slots={{ footer: TicketListFooter }}
          slotProps={{ footer: ticketListFooterSlotProps }}
          getRowClassName={params => (params?.row?.isVoided ? 'voided' : '')}
        />
      </S.GridStyle>
    </S.FormStyle>
  );
};

export default useThemeConfig(TicketList);
